/**
 * PWA installation, and the platform reality behind the placement ritual.
 *
 * The ritual (spec §6.1) asks the user to install Comeback and put it in the
 * exact home-screen slot their problem app occupies. Every part of that is an
 * OS flow we cannot drive:
 *
 * - **iOS Safari** has no install API at all. `beforeinstallprompt` does not
 *   exist there and never has, so iOS gets written steps and nothing else.
 * - **Android Chrome** fires `beforeinstallprompt`, which we capture and can
 *   replay from a button — but only once, and only if the browser decided the
 *   app was installable.
 * - **Dragging an icon into a particular slot** is not scriptable anywhere.
 *
 * So this module tells the UI which set of instructions to show, and offers the
 * native prompt when there is one. The rest is words. That limitation is also
 * why H1 is an open question rather than a settled design detail.
 */

export type Platform = 'ios' | 'android' | 'desktop';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

export function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as a Mac; the touch-point check is the standard way to
  // tell an iPad from a desktop Safari, and getting it wrong would show someone
  // instructions for a browser they are not using.
  const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(ua) || iPadOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

export function isStandalone(): boolean {
  // `display-mode: standalone` says the app was launched as an installed app.
  // It says nothing about *why* it was opened — which is exactly why no
  // detection source is inferred from it (architecture §2.9).
  return (
    globalThis.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function watchForInstallPrompt(onAvailable: (available: boolean) => void): () => void {
  const capture = (event: Event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    onAvailable(true);
  };
  const installed = () => {
    deferredPrompt = null;
    onAvailable(false);
  };

  addEventListener('beforeinstallprompt', capture);
  addEventListener('appinstalled', installed);
  return () => {
    removeEventListener('beforeinstallprompt', capture);
    removeEventListener('appinstalled', installed);
  };
}

export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

/** Returns true if the user accepted. The prompt can only be shown once. */
export async function promptInstall(): Promise<boolean> {
  if (!deferredPrompt) return false;
  const prompt = deferredPrompt;
  deferredPrompt = null;
  await prompt.prompt();
  const { outcome } = await prompt.userChoice;
  return outcome === 'accepted';
}
